<template>
  <section v-if="popularData.length" class="mb-8">
    <h1 v-if="title" class="fz-24 mb-5">
      {{ title }}
    </h1>

    <swiper :slides-per-view="'auto'">
      <swiper-slide v-for="(data, index) in formattedData" :key="data.id">
        <div class="top-number">
          {{ index + 1 }}
        </div>

        <ion-card
          color="medium"
          class="podcast-card pointer"
          :style="getBackgroundColor(getImage(formattedPodcastData(data)))"
          @click="openPodcast(data)"
        >
          <AppImg
            :img-src="getImage(formattedPodcastData(data))?.url"
            :img-alt="`top${index + 1} podcast image`"
            border-radius="10px"
            class="podcast-card__img"
            is-transparent
          />

          <ion-card-header class="podcast-card__header">
            <ion-card-subtitle class="podcast-card__header__subtitle">{{
              getStreamTitle(data.series_title)
            }}</ion-card-subtitle>
            <ion-card-title class="podcast-card__header__title">{{
              getStreamTitle(data.stream_title)
            }}</ion-card-title>
          </ion-card-header>

          <ion-label class="podcast-card__duration"
            >{{ Math.round(data.duration / 60) }}
            {{ $t('podcast.min') }}</ion-label
          >
        </ion-card>
      </swiper-slide>
    </swiper>
  </section>
</template>

<script setup>
import {
  IonCard,
  IonCardHeader,
  IonCardSubtitle,
  IonCardTitle,
  IonLabel,
} from '@ionic/vue';
import { computed } from 'vue';
import { useStore } from 'vuex';

import { getSmallestSizeImage } from '@/helpers/functions';
import AppImg from '@/components/common/AppImg.vue';

import { Swiper, SwiperSlide } from 'swiper/vue';
import 'swiper/css';

import useBackgroundColor from '@/composables/useBackgroundColor';

import {
  CATEGORY_GENERAL_MEDIA,
  CATEGORY_BANNER_MEDIA,
  CATEGORY_STREAM_STILL,
  CATEGORY_PODCAST_IMG,
} from '@/config/constants';

const PRIOR_IMAGES = [
  CATEGORY_PODCAST_IMG,
  CATEGORY_STREAM_STILL,
  CATEGORY_BANNER_MEDIA,
  CATEGORY_GENERAL_MEDIA,
];

const props = defineProps({
  title: {
    type: String,
    default: null,
  },
  popularData: {
    type: Array,
    default: () => [],
  },
});

const formattedData = computed(() =>
  props.popularData.map((data) => ({
    ...data,
    course_media: data.course?.media ?? [],
  })),
);

const formattedPodcastData = (data) =>
  Object.assign({}, data, { course_media: data.course.media });

function getImage(data) {
  const validStreamImages = data.media.filter((media) =>
    PRIOR_IMAGES.includes(media.category),
  );

  validStreamImages.sort((a, b) => b.category - a.category);
  const streamImg = getSmallestSizeImage(validStreamImages) ?? null;
  const courseImg = getSmallestSizeImage(data.course_media) ?? null;

  return streamImg || courseImg;
}

function getStreamTitle(title) {
  if (title.length > 70) {
    return title.slice(0, 70) + '...';
  }

  return title;
}

const store = useStore();

const openPlayer = (id, course) => {
  const startAt = course.streams.findIndex((stream) => stream.id === id);
  store.dispatch('player/openPlayer', {
    course,
    company: course.company,
    playlist: course.streams,
    startAt,
  });
};

const openPodcast = (data) => {
  store.dispatch('course/setCourseData', data.course_id).then((course) => {
    openPlayer(data.id, course);
  });
};

const { getDarkestColors } = useBackgroundColor();

function getBackgroundColor(image) {
  const palette = image?.vibrant_palette_json;
  const { primary } = getDarkestColors(palette);

  return {
    background: primary,
  };
}
</script>

<style scoped lang="scss">
@use '@/theme/custom' as *;

.swiper-slide {
  position: relative;
  max-width: 310px !important;
  margin: 0 10px;

  &:last-of-type {
    .top-number {
      width: 60px;
      height: 60px;
    }
  }

  .top-number {
    width: 50px;
    height: 50px;
    background-color: black;
    border-radius: 50%;
    display: flex;
    justify-content: center;
    align-items: center;
    font-size: 42px;
    z-index: 1;
    position: absolute;
    top: 0;
    left: -10px;
  }

  .podcast-card {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    position: relative;
    padding: 15px 20px 5px;
    border-radius: 20px;
    height: 310px;
    background-color: #1e1e1e;

    &__img {
      width: 100%;
      height: 180px;
      display: flex;
      justify-content: center;

      &:deep(img) {
        max-width: 180px;
        height: 100%;
      }
    }

    &__header {
      display: flex;
      flex-direction: column;
      padding: 20px 0 5px 0;

      &__subtitle {
        text-align: left;
        font-size: 10px;
        font-weight: 300;
      }

      &__title {
        text-align: left;
        font-size: 14px;
      }
    }

    &__duration {
      font-size: 10px;
      font-weight: 500;
      margin-left: auto;
      margin-bottom: 5px;
    }
  }
}

@media (max-width: $tablet-breakpoint) {
  .swiper-slide {
    max-width: 180px !important;
    margin: 0;

    &:last-of-type {
      .top-number {
        width: 40px;
        height: 40px;
      }
    }

    .top-number {
      width: 30px;
      height: 30px;
      font-size: 26px;
      top: 0px;
      left: 0px;
    }

    .podcast-card {
      padding: 10px;
      height: 200px;
      margin: 10px;
      position: relative;

      &__img {
        height: 100px;
        &:deep(img) {
          max-width: 100px;
        }
      }

      &__header {
        padding: 10px 0 5px 0;

        &__subtitle {
          font-size: 8px;
        }

        &__title {
          font-size: 10px;
        }
      }
      &__duration {
        position: absolute;
        bottom: 0px;
        right: 10px;
      }
    }
  }
}
</style>
